// ---------------------------------------------------------------------------
// Price Forecast — multi-signal projection with confidence bands
// ---------------------------------------------------------------------------
//
// This is a statistical/technical projection, not an ML model. It blends:
//
//   1. Trend component   — linear regression slope of log returns (drift)
//   2. Momentum component — short-term EMA(fast) vs EMA(slow) divergence
//   3. Mean reversion     — distance from long-term MA pulls price back
//   4. Volatility band    — std dev of log returns → widening uncertainty cone
//   5. Liquidity gravity  — nearby order book walls act as magnets/barriers
//
// The forecast output is a center line + upper/lower confidence bounds that
// widen with sqrt(time), reflecting rising uncertainty over longer horizons.
//
// Inputs: historical OHLCV and optional order book snapshot.
// Output: array of forecast points with time, price, upper, lower.

import type { OHLCV } from "./indicators";
import { ema } from "./indicators";

export interface ForecastPoint {
  time: number;
  price: number;
  upper: number;
  lower: number;
}

export interface LiquidityLevel {
  price: number;
  notional: number;
  side: "bid" | "ask";
}

export interface ForecastConfig {
  /** Number of future bars to forecast */
  horizon: number;
  /** Seconds between bars (inferred from data if omitted) */
  intervalSeconds?: number;
  /** Lookback for regression/trend (default 60) */
  lookback?: number;
  /** Confidence multiplier (σ): 1.0 ≈ 68%, 1.645 ≈ 90%, 2.0 ≈ 95% */
  confidenceSigma?: number;
  /** Order book walls for liquidity gravity effect */
  liquidity?: LiquidityLevel[];
}

export interface ForecastResult {
  points: ForecastPoint[];
  /** Short summary for UI tooltip */
  summary: {
    trend: "bullish" | "bearish" | "neutral";
    driftPctPerBar: number;
    volatilityPctPerBar: number;
    targetPrice: number;
    targetChangePct: number;
    confidencePct: number;
  };
}

// ---------- Helpers ----------

/** Linear regression of y = a + bx over indexed points → returns slope b */
function linearRegressionSlope(values: number[]): number {
  const n = values.length;
  if (n < 2) return 0;
  const xMean = (n - 1) / 2;
  const yMean = values.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xMean) * (values[i]! - yMean);
    den += (i - xMean) ** 2;
  }
  return den === 0 ? 0 : num / den;
}

/** Std dev of a numeric array */
function stdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance =
    values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

// ---------- Main forecast function ----------

export function forecastPrice(
  data: OHLCV[],
  config: ForecastConfig,
): ForecastResult | null {
  const {
    horizon,
    lookback = 60,
    confidenceSigma = 1.645, // 90% CI
    liquidity,
  } = config;

  if (data.length < lookback + 5) return null;

  const recent = data.slice(-lookback);
  const lastBar = recent[recent.length - 1]!;
  const currentPrice = lastBar.close;
  const lastTime = lastBar.time;

  // Infer interval from the last few bars
  const intervalSeconds =
    config.intervalSeconds ??
    (recent.length >= 2
      ? recent[recent.length - 1]!.time - recent[recent.length - 2]!.time
      : 3600);

  // --- 1. Log returns ---
  const logReturns: number[] = [];
  for (let i = 1; i < recent.length; i++) {
    const prev = recent[i - 1]!.close;
    const curr = recent[i]!.close;
    if (prev > 0 && curr > 0) {
      logReturns.push(Math.log(curr / prev));
    }
  }

  // --- 2. Trend: regression slope of log prices ---
  const logPrices = recent.map((d) => Math.log(d.close));
  const trendSlope = linearRegressionSlope(logPrices); // drift per bar (in log space)

  // --- 3. Momentum: EMA fast vs slow divergence ---
  const emaFast = ema(recent, 9);
  const emaSlow = ema(recent, 21);
  const lastFast = emaFast[emaFast.length - 1]?.value ?? currentPrice;
  const lastSlow = emaSlow[emaSlow.length - 1]?.value ?? currentPrice;
  const momentum = lastSlow > 0 ? (lastFast - lastSlow) / lastSlow : 0;
  // Momentum contribution — small nudge to drift
  const momentumDrift = momentum * 0.15;

  // --- 4. Mean reversion: distance from long-term mean ---
  const longMean =
    recent.reduce((a, b) => a + b.close, 0) / recent.length;
  const deviation = (currentPrice - longMean) / longMean;
  // Strong deviation pulls forecast back toward mean (inverse signal)
  const reversionPull = -deviation * 0.05;

  // --- 5. Volatility (log return std dev) ---
  const volPerBar = stdDev(logReturns);

  // Combined drift per bar (log space)
  const totalDrift = trendSlope + momentumDrift + reversionPull;

  // --- 6. Build the forecast path ---
  const points: ForecastPoint[] = [];
  let projectedLogPrice = Math.log(currentPrice);

  for (let i = 1; i <= horizon; i++) {
    projectedLogPrice += totalDrift;
    let projectedPrice = Math.exp(projectedLogPrice);

    // --- 7. Liquidity gravity: nearby walls attract price ---
    if (liquidity && liquidity.length > 0) {
      const maxNotional = Math.max(...liquidity.map((l) => l.notional));
      if (maxNotional > 0) {
        // For each wall within 3% of projected price, nudge toward it
        let nudge = 0;
        let totalWeight = 0;
        for (const wall of liquidity) {
          const distPct = Math.abs(projectedPrice - wall.price) / projectedPrice;
          if (distPct > 0.03) continue;
          // Strength decays with distance, scales with notional
          const strength =
            (wall.notional / maxNotional) * (1 - distPct / 0.03);
          // Bids (support) pull up if below, asks (resistance) pull down if above
          const direction = wall.price - projectedPrice;
          nudge += direction * strength;
          totalWeight += strength;
        }
        if (totalWeight > 0) {
          // Weighted nudge, damped so it doesn't override trend entirely
          const nudgeAmount = (nudge / totalWeight) * 0.08;
          projectedPrice += nudgeAmount;
          projectedLogPrice = Math.log(projectedPrice);
        }
      }
    }

    // --- 8. Confidence band widens with sqrt(time) ---
    const bandWidth = volPerBar * confidenceSigma * Math.sqrt(i);
    const upper = projectedPrice * Math.exp(bandWidth);
    const lower = projectedPrice * Math.exp(-bandWidth);

    points.push({
      time: lastTime + intervalSeconds * i,
      price: projectedPrice,
      upper,
      lower,
    });
  }

  // --- Summary ---
  const lastPoint = points[points.length - 1]!;
  const targetChangePct =
    ((lastPoint.price - currentPrice) / currentPrice) * 100;
  const trend: "bullish" | "bearish" | "neutral" =
    Math.abs(targetChangePct) < 1
      ? "neutral"
      : targetChangePct > 0
        ? "bullish"
        : "bearish";

  // Confidence % — inversely proportional to band width at horizon
  const bandPct =
    ((lastPoint.upper - lastPoint.lower) / lastPoint.price) * 100;
  const confidencePct = Math.max(0, Math.min(100, 100 - bandPct * 2));

  return {
    points,
    summary: {
      trend,
      driftPctPerBar: (Math.exp(totalDrift) - 1) * 100,
      volatilityPctPerBar: (Math.exp(volPerBar) - 1) * 100,
      targetPrice: lastPoint.price,
      targetChangePct,
      confidencePct,
    },
  };
}
